import { LatLngBounds, LatLngExpression, LatLngTuple, Polygon, Polyline, latLng } from 'leaflet'
import type { Polygon as GeoJSONPolygon, MultiPolygon } from 'geojson'
import wicket from 'wicket/wicket-leaflet'
import polylabel from 'polylabel'
import area from '@turf/area'
import { polygon } from '@turf/helpers'
import { MapCadastralPlotFragment } from '@foros-fe/graphql'

type WktPolygon = {
  merge(wktPolygon: WktPolygon): WktPolygon
  toObject(): Polygon
  toJson(): GeoJSONGeometry
}
type WktString = string | undefined | null
export type GeoJSONGeometry = MultiPolygon | GeoJSONPolygon
export type GeoJSONData = [LatLngBounds | undefined, GeoJSONGeometry[]]
type GeospatialCadastralPlot = Pick<MapCadastralPlotFragment, 'boundary' | 'latitude' | 'longitude'>

const minimumLabelablePolygonArea = 100

export function polygonStringsToGeoJSONData(polygonArray: WktString[]): GeoJSONData {
  const polygons = polygonArray.filter((item): item is string => !!item)

  if (!polygons.length) {
    return [, []]
  }

  const wktPolygons = polygons.map(stringToWktInstance)
  const geoJSON = wktPolygons.map((wktPolygon) => wktPolygon.toJson())

  return [
    wktPolygonToBounds(
      wktPolygons.reduce((rootWktPolygon, wktPolygon) => rootWktPolygon.merge(wktPolygon))
    ),
    geoJSON,
  ]
}

export function getCadastralPlotCenter(cadastralPlot?: GeospatialCadastralPlot | null) {
  if (!cadastralPlot) return

  const { boundary, latitude, longitude } = cadastralPlot

  let bounds
  if (boundary) {
    bounds = wktPolygonToBounds(stringToWktInstance(boundary))
  } else if (latitude && longitude) {
    bounds = new LatLngBounds([[latitude, longitude]])
  }

  return bounds?.getCenter()
}

export function stringToWktInstance(polygon: NonNullable<WktString>): WktPolygon {
  return new wicket.Wkt(polygon)
}

export function wktPolygonToBounds(wktPolygon: WktPolygon) {
  return wktPolygon.toObject().getBounds()
}

export function cadastralPlotsToGeoJSONData(
  cadastralPlots: GeospatialCadastralPlot[]
): GeoJSONData {
  const [boundaryBounds, boundaryGeoJSON] = polygonStringsToGeoJSONData(
    cadastralPlots.map(({ boundary }) => boundary)
  )

  const latLonArray: [number, number][] = cadastralPlots
    .filter(({ boundary, latitude, longitude }) => !boundary && latitude && longitude)
    .map(({ latitude, longitude }) => [latitude!, longitude!])
  const [latLonBounds, latLongGeoJSON] = [
    latLonArray.length ? new Polyline(latLonArray).getBounds() : undefined,
    latLonArray.map((latlon) => new Polygon([latlon]).toGeoJSON().geometry),
  ]

  return [
    boundaryBounds && latLonBounds
      ? boundaryBounds.extend(latLonBounds)
      : boundaryBounds || latLonBounds,
    [...boundaryGeoJSON, ...latLongGeoJSON],
  ]
}

export function googleDirectionsURL(coords: LatLngExpression) {
  const point = latLng(coords)

  return `https://www.google.com/maps/dir/?api=1&origin=&destination=${point.lat},${point.lng}&travelmode=driving&dir_action=navigate`
}

export function geoJSONPolylabelPositions(feature: GeoJSONGeometry) {
  const polygons = feature.type === 'Polygon' ? [feature.coordinates] : feature.coordinates

  return polygons
    .filter((coords) => area(polygon(coords)) >= minimumLabelablePolygonArea)
    .map(polygonPolylabelPosition)
}

export function polygonPolylabelPosition(coords: GeoJSONGeometry['coordinates']): LatLngTuple {
  const [lng, lat] = polylabel(coords as number[][][])

  return [lat, lng]
}
